import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import type { CustomFloatingFilterDisplayProps } from 'ag-grid-react';
import { DatePicker, Input } from 'antd';
import * as sagaActions from '../sagaActions';
import * as query from '../query';
import { useDispatch, useSelector } from 'react-redux';
import { getGridFilters, getGridFiltersByColumn } from 'models/dynamicGrid/selector';
import _ from 'lodash';
import { getState } from 'reducers';
import { formatDateTz } from 'utils/utilities';
import { usePopupParent } from 'hooks/usePopupParent';

export interface CustomProps extends CustomFloatingFilterDisplayProps {
	type: string;
	colDef: any;
}

const CustomFloatingFilter = ({ model, onModelChange, type, colDef, api }: CustomProps) => {
	const [inputValue, setinputValue] = useState<any>('');
	const selectedCriteria = query.getSearchParameters(colDef ?? {});
	const tableId = colDef.AppTableID;
	const dispatch = useDispatch();
	const getPopupParent = usePopupParent('full-screen');

	const searchQueriesDefault = useSelector((state) => getGridFiltersByColumn(state, tableId, colDef.AppColumnName));
	const searchQueriesAppliedPrev = _.find(searchQueriesDefault, (val) => val.criteria.rowIdentifier);
	const selectTypesDefault = query.getCriteriaOperators(selectedCriteria) ?? [];
	const selectTypeDefault = selectTypesDefault.find((s) => s.value === 'like');

	const searchQueries = {
		query: searchQueriesAppliedPrev ? searchQueriesAppliedPrev.query : '',
		operator: selectTypeDefault ?? selectTypesDefault[0],
	};

	const searchQueriesAll = getGridFilters(getState(), tableId);

	const updateQuery = (searchInput: any) => {
		const rowIdentifier = selectedCriteria?.rowIdentifier;

		// find the current query in state
		const searchQuery = searchQueriesAll?.search.find((q) => q?.criteria?.rowIdentifier === rowIdentifier);

		// get queries from filters for the same column
		const queries = getGridFiltersByColumn(getState(), tableId, rowIdentifier);

		// pick one that isn’t the same as current
		const updated = _.find(queries, (q) => q.id !== searchQuery?.id);

		// the candidate to remove/update
		const queryToRemove = updated || searchQuery;

		if (searchInput) {
			// new input provided
			if (queryToRemove) {
				dispatch(
					sagaActions.removeQuery({
						tableId,
						query: { ...queryToRemove, query: searchInput },
					})
				);
			}

			dispatch(
				sagaActions.updateQueries({
					selectedCriteria,
					searchInput,
					tableId,
					selectedOperator: searchQueries.operator,
				})
			);
		} else {
			// input cleared → just remove
			if (queryToRemove) {
				dispatch(
					sagaActions.removeQuery({
						tableId,
						query: { ...queryToRemove, query: searchInput },
					})
				);
			}
			console.log(selectedCriteria);
			// If needed, we can also send explicit remove to saga:
			// dispatch(
			//   sagaActions.updateQueries({
			//     selectedCriteria,
			//     searchInput,
			//     tableId,
			//     selectedOperator: queryToRemove?.operator,
			//     remove: true,
			//   })
			// );
		}
	};

	const debouncedUpdateQuery = useMemo(() => _.debounce((val) => updateQuery(val), 500), []);

	const onInput = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
		debouncedUpdateQuery(value);
	};

	useEffect(() => {
		let cleanedQuery = '';

		if (typeof searchQueries?.query === 'string') {
			// Case 1: string → clean '
			cleanedQuery = searchQueries.query.replace(/'/g, '');
		} else if (
			typeof searchQueries?.query === 'object' &&
			searchQueries?.query !== null &&
			!Array.isArray(searchQueries?.query)
		) {
			// Case 2: object → take first value
			const [firstValue] = Object.values(searchQueries.query);
			cleanedQuery = String(firstValue ?? '');
		}

		setinputValue(cleanedQuery);

		return () => {
			setinputValue('');
			debouncedUpdateQuery.cancel();
		};
	}, [searchQueries.query, debouncedUpdateQuery]);

	if (colDef.ColumnType === 'date') {
		return (
			<div className="antd-date-picker">
				<DatePicker
					getPopupContainer={getPopupParent}
					value={inputValue ? formatDateTz(inputValue) : null}
					onChange={(date, dateString) => {
						setinputValue(date);
						debouncedUpdateQuery(dateString);
					}}
					format={'L'}
					allowClear
				/>
			</div>
		);
	}

	return (
		<Input
			type="text"
			value={inputValue}
			onChange={(e) => {
				setinputValue(e.target.value);
				onInput(e);
			}}
		/>
	);
};

export default CustomFloatingFilter;
